import { Redirect } from "react-router-dom";
import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
// import axios
import axios from "axios";
import userStore from "../Stores/User";
import { observer } from "mobx-react";
// Import loading gif
import loadingIcon from "../Assets/loading.gif";
import mixpanel from "mixpanel-browser";
import { useStripe as useStripeContext } from "../StripeProvider";
import { validateCoupon } from "../audiblogsApi";
import Loader from "../Components/Loader";

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_API_KEY);

const appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#fff",
    colorBackground: "rgba(24,15,34,.85)",
    colorText: "#fff",
    colorDanger: "#df1b41",
    fontFamily: "Ideal Sans, system-ui, sans-serif",
    spacingUnit: "2px",
    borderRadius: "4px",
  },
  rules: {
    ".Input": {
      color: "#fff",
    },
    ".Terms": {
      display: "none",
    },
  },
  labels: "floating",
};

// Helper function to format coupon message
const formatCouponMessage = (couponDetails) => {
  let message = "";

  // Format the discount amount
  if (couponDetails.percent_off) {
    message = `${couponDetails.percent_off}% off`;
  } else if (couponDetails.amount_off) {
    const amount = (couponDetails.amount_off / 100).toFixed(2);
    const currency = couponDetails.currency.toUpperCase();
    message = `${currency} ${amount} off`;
  }

  // Add duration information
  if (
    couponDetails.duration === "repeating" &&
    couponDetails.duration_in_months
  ) {
    message += ` for ${couponDetails.duration_in_months} months`;
  } else if (couponDetails.duration === "once") {
    message += ` (one-time)`;
  } else if (couponDetails.duration === "forever") {
    message += ` forever`;
  }

  // Add minimum amount restriction if present
  if (couponDetails.restrictions?.minimum_amount) {
    const minAmount = (couponDetails.restrictions.minimum_amount / 100).toFixed(
      2
    );
    const currency = couponDetails.restrictions.minimum_amount_currency.toUpperCase();
    message += ` (min. ${currency} ${minAmount})`;
  }

  return message || "Coupon applied successfully!";
};

// Initial form without Stripe hooks
const InitialCheckoutForm = observer(
  ({
    onCouponSubmit,
    couponError,
    couponSuccess,
    onAddPaymentDetails,
    isLoading,
    autoAppliedCoupon,
  }) => {
    const [couponCode, setCouponCode] = useState("");

    const handleCouponChange = (event) => {
      setCouponCode(event.target.value);
    };

    const handleCouponApply = () => {
      if (!couponCode.trim()) return;
      onCouponSubmit(couponCode.trim());
    };

    return (
      <div
        style={{
          maxWidth: "400px",
          margin: "0 auto",
        }}
      >
        {!autoAppliedCoupon && (
          <div
            style={{
              marginTop: "20px",
              marginBottom: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <input
              type="text"
              value={couponCode}
              onChange={handleCouponChange}
              placeholder="Coupon code"
              style={{
                flex: 1,
                padding: "8px 12px",
                backgroundColor: "rgba(24,15,34,1)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              onClick={handleCouponApply}
              disabled={isLoading}
              style={{
                padding: "8px 12px",
                backgroundColor: "transparent",
                color: "#fff",
                border: "1px solid #fff",
                borderRadius: "4px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                opacity: isLoading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Apply
              {isLoading && (
                <img
                  src={loadingIcon}
                  alt="Loading..."
                  style={{ width: "16px", height: "16px" }}
                />
              )}
            </button>
          </div>
        )}

        {!autoAppliedCoupon && couponError && (
          <div
            style={{
              color: "#df1b41",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✕</span> {couponError}
          </div>
        )}

        {!autoAppliedCoupon && couponSuccess && (
          <div
            style={{
              color: "#4ade80",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✓</span> {couponSuccess}
          </div>
        )}

        <button
          type="button"
          onClick={onAddPaymentDetails}
          style={{
            width: "100%",
            minHeight: "44px",
            background: "linear-gradient(90deg, #4F46E5 0%, #E11D48 100%)",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "16px",
            padding: "10px 20px",
            marginTop: "15px",
            marginBottom: "15px",
          }}
        >
          Add Payment Details
        </button>
      </div>
    );
  }
);

// Payment form with Stripe hooks
const PaymentCheckoutForm = observer(
  ({ onCouponSubmit, couponError, couponSuccess, onBack, setupIntentId, autoAppliedCoupon, onAddPaymentDetails, isLoading }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [couponCode, setCouponCode] = useState("");

    useEffect(() => {
      // Wait for the payment element to be rendered
      const timer = setTimeout(() => {
        const paymentElement = document.querySelector(".StripeElement");
        if (paymentElement) {
          paymentElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }, []);

    const handleCouponChange = (event) => {
      setCouponCode(event.target.value);
    };

    const handleCouponApply = () => {
      if (!couponCode.trim()) return;
      onCouponSubmit(couponCode.trim());
      setCouponCode("");
    };

    const handleSubmit = async (event) => {
      event.preventDefault();

      // Early return if already processing or missing dependencies
      if (isProcessing || !stripe || !elements) {
        return;
      }

      setIsProcessing(true);

      try {
        // Refresh token before confirming setup
        await userStore.refreshIdToken();
        
        // Do a $0.10 preauthorization to verify card has funds
        const { error: setupError, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/add`,
            payment_method_data: {
              billing_details: {
                name: userStore.user?.displayName || '',
                email: userStore.user?.email || ''
              }
            }
          }
        });

        if (setupError) {
          let errorMessage = setupError.message;
          
          // Provide more user-friendly error messages
          if (setupError.type === 'card_error') {
            if (setupError.code === 'card_declined') {
              errorMessage = "Your card was declined. Please try a different card or contact your bank.";
            } else if (setupError.code === 'insufficient_funds') {
              errorMessage = "Your card has insufficient funds. Please try a different card.";
            } else if (setupError.code === 'expired_card') {
              errorMessage = "Your card has expired. Please use a different card.";
            }
          }
          
          setMessage(errorMessage);
          mixpanel.track("Premium trial failed", {
            error: setupError.message,
            error_type: setupError.type,
            error_code: setupError.code
          });
          return;
        }

        // If setup succeeds, card is verified and ready for future charges
        if (setupIntent && setupIntent.status === 'succeeded') {
          setMessage("Card verified! Starting your trial...");
        }

        // If we get here, payment was successful and card is verified
        // Show success message briefly before redirecting
        setTimeout(() => {
          window.location.href = `${window.location.origin}/add`;
        }, 1500);
      } catch (e) {
        setMessage("An unexpected error occurred.");
        mixpanel.track("Premium trial failed", {
          error: e.message,
          error_type: "unexpected_error"
        });
      } finally {
        setIsProcessing(false);
      }
    };

    return (
      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: "400px",
          margin: "0 auto",
        }}
      >
        {/* Coupon Feedback Messages */}
        {couponError && (
          <div
            style={{
              color: "#df1b41",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✕</span> {couponError}
          </div>
        )}

        {couponSuccess && (
          <div
            style={{
              color: "#4ade80",
              fontSize: "14px",
              marginBottom: "15px",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <span style={{ fontWeight: "bold" }}>✓</span> {couponSuccess}
          </div>
        )}

        {/* Show loading when creating new setup intent after coupon/plan change */}
        {isLoading && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            color: "#888"
          }}>
            <img src={loadingIcon} alt="Loading..." style={{ width: "20px", height: "20px", marginRight: "10px" }} />
            Updating payment form...
          </div>
        )}

        {/* Coupon Input Form */}
        {!autoAppliedCoupon && (
          <div
            style={{
              marginTop: "20px",
              marginBottom: "20px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <input
              type="text"
              value={couponCode}
              onChange={handleCouponChange}
              placeholder="Coupon code"
              style={{
                flex: 1,
                padding: "8px 12px",
                backgroundColor: "rgba(24,15,34,1)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              onClick={handleCouponApply}
              disabled={isLoading}
              style={{
                padding: "8px 12px",
                backgroundColor: "transparent",
                color: "#fff",
                border: "1px solid #fff",
                borderRadius: "4px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "14px",
                opacity: isLoading ? 0.7 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              Apply
              {isLoading && (
                <img
                  src={loadingIcon}
                  alt="Loading..."
                  style={{ width: "16px", height: "16px" }}
                />
              )}
            </button>
          </div>
        )}

        <PaymentElement />

        <div
          style={{
            marginTop: "15px",
          }}
        >
          <button
            disabled={isProcessing || !stripe || !elements}
            className="cta-button"
            type="submit"
            style={{
              width: "100%",
              minHeight: "44px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(90deg, #4F46E5 0%, #E11D48 100%)",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "16px",
              padding: "10px 20px",
            }}
          >
            {isProcessing ? (
              <img
                src={loadingIcon}
                alt="Loading..."
                style={{ width: "20px", height: "20px" }}
              />
            ) : (
              "Start trial for $0"
            )}
          </button>
        </div>
        {message && (
          <div
            style={{
              color: "#df1b41",
              textAlign: "center",
              marginTop: "10px",
              marginBottom: "10px",
              fontSize: "14px",
            }}
          >
            {message}
          </div>
        )}
      </form>
    );
  }
);

const StripeCheckout = observer(
  ({ planType, onSetupIntentCreated, onShowPaymentElement, autoAppliedCoupon, immediateSetupIntent = false }) => {
    const [clientSecret, setClientSecret] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [couponError, setCouponError] = useState("");
    const [couponSuccess, setCouponSuccess] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isCouponLoading, setIsCouponLoading] = useState(false);
    const [error, setError] = useState(null);
    const [couponDetails, setCouponDetails] = useState(null);
    const [showPaymentElement, setShowPaymentElement] = useState(false);
    const [previousSetupIntentId, setPreviousSetupIntentId] = useState(() =>
      localStorage.getItem("lastSetupIntentId")
    );
    const [isRefreshing, setIsRefreshing] = useState(false);

    const stripe = useStripeContext();

    // Notify parent when showPaymentElement changes
    useEffect(() => {
      console.log('CheckoutForm: showPaymentElement changed to', showPaymentElement, 'isRefreshing:', isRefreshing);
      onShowPaymentElement?.(showPaymentElement);
    }, [showPaymentElement, onShowPaymentElement, isRefreshing]);

    // Automatically create setup intent if immediateSetupIntent is true
    useEffect(() => {
      if (immediateSetupIntent && !clientSecret && !isLoading && !isRefreshing) {
        console.log('Creating initial setup intent...');
        createSetupIntent();
      }
    }, [immediateSetupIntent, clientSecret, isLoading, isRefreshing]);

    // Watch for plan type changes and refresh setup intent if needed
    useEffect(() => {
      if (clientSecret && showPaymentElement && !isLoading && !isRefreshing) {
        console.log('Plan type changed, refreshing setup intent...', planType);
        // Plan type changed, refresh the setup intent
        setIsRefreshing(true);
        // Clear the current client secret to trigger the refresh
        setClientSecret("");
        // The payment element will show the loading state while refreshing
        setTimeout(() => createSetupIntent(), 100);
      }
    }, [planType]);

    const createSetupIntent = useCallback(async () => {
      try {
        console.log('Creating setup intent for plan:', planType);
        setIsLoading(true);
        setError(null);

        let token = "";
        await new Promise((resolve) => {
          window.grecaptcha.enterprise.ready(async () => {
            token = await window.grecaptcha.enterprise.execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, {action: 'LOGIN'});
            resolve();
          });
        });

        console.log("recaptcha token", token);
        
        const headers = { "Audiblogs-Id-Token": userStore.idToken };

        // Get UTM params from localStorage
        const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');

        const data = {
          metadata: {
            // Stored UTM params (from landing page)
            ...storedUtmParams,
            // Mixpanel UTM params (as fallback)
            utm_source: storedUtmParams.utm_source || mixpanel.get_property("utm_source"),
            utm_medium: storedUtmParams.utm_medium || mixpanel.get_property("utm_medium"),
            utm_campaign: storedUtmParams.utm_campaign || mixpanel.get_property("utm_campaign"),
            utm_term: storedUtmParams.utm_term || mixpanel.get_property("utm_term"),
            utm_content: storedUtmParams.utm_content || mixpanel.get_property("utm_campaign"),
            // Other tracking params
            search_engine: mixpanel.get_property("$search_engine"),
            search_keyword: mixpanel.get_property("mp_keyword"),
            referrer: storedUtmParams.referrer || mixpanel.get_property("$referrer"),
            initial_referrer: mixpanel.get_property("$initial_referrer"),
            tolt_referral: window.tolt_referral,
            subscription_type: planType,
            coupon: couponCode || "",
            recaptcha_token: token,
            landing_page_variant: localStorage.getItem('landing_page_variant') || 'A',
          },
          setup_intent_id: previousSetupIntentId,
        };

        console.log('Setup intent request data:', data);

        const result = await axios.post(
          process.env.REACT_APP_BACKEND_URL +
            "/api/payments/create_setup_intent",
          data,
          { headers }
        );

        if (result.data.error) {
          setError(result.data.error);
        } else if (result.data && result.data.client_secret) {
          const newSetupIntentId = result.data.setup_intent_id;
          setPreviousSetupIntentId(newSetupIntentId);
          localStorage.setItem("lastSetupIntentId", newSetupIntentId);
          setClientSecret(result.data.client_secret);
          // Only set showPaymentElement to true if we're not refreshing
          // This prevents overriding the parent's modal state during refresh
          if (!isRefreshing) {
            setShowPaymentElement(true);
          }
          setIsRefreshing(false); // Reset refreshing state
          onSetupIntentCreated?.(newSetupIntentId);
        } else {
          setError("Invalid response from server");
        }
      } catch (error) {
        console.error("Error creating setup intent:", error);
        const errorMessage =
          "We detected some unusual behavior. Please try again and contact support if the issue persists.";
        setError(errorMessage);
        setIsRefreshing(false); // Reset refreshing state on error
      } finally {
        setIsLoading(false);
      }
    }, [planType, couponCode, previousSetupIntentId, userStore.idToken]);

    const handleCouponSubmit = async (code) => {
      if (!code.trim()) return;

      try {
        setIsCouponLoading(true);
        setCouponError("");
        setCouponSuccess("");
        setCouponDetails(null);

        const result = await validateCoupon(userStore.idToken, code.trim());

        if (result.error) {
          setCouponError(result.error);
          setCouponCode("");
        } else if (result.valid && result.coupon_details) {
          setCouponDetails(result.coupon_details);
          setCouponSuccess(formatCouponMessage(result.coupon_details));
          setCouponCode(code.trim());
          
          // If we have a valid coupon and a setup intent exists, we need to create a new one
          // with the coupon applied to ensure it's properly attached
          if (clientSecret) {
            setIsRefreshing(true); // Mark as refreshing
            setClientSecret(""); // Clear current setup intent
            // Keep modal open - don't change showPaymentElement state
            // Create new setup intent with coupon
            setTimeout(() => createSetupIntent(), 100);
          }
        } else {
          setCouponError("Invalid coupon code");
          setCouponCode("");
        }
      } catch (error) {
        console.error("Error handling coupon:", error);
        setCouponError("Failed to validate coupon");
        setCouponCode("");
      } finally {
        setIsCouponLoading(false);
      }
    };

    const handleAddPaymentDetails = () => {
      createSetupIntent();

      // Add a second check after more time
      setTimeout(() => {
        const paymentElement = document.querySelector(".StripeElement");
        if (paymentElement) {
          window.scrollTo({
            top: paymentElement.offsetTop - 50,
            behavior: "smooth",
          });
        }
      }, 2000);
    };

    const handleBack = () => {
      // Only close payment element if we're not refreshing
      if (!isRefreshing) {
        setShowPaymentElement(false);
      }
    };

    // Check if user has an active subscription
    const hasActiveSubscription =
      userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id;

    // Clear setup intent ID from localStorage when payment is successful
    useEffect(() => {
      if (userStore.user?.subscriptions?.raw_subscriptions?.[0]?.plan?.id) {
        localStorage.removeItem("lastSetupIntentId");
      }
    }, [userStore.user?.subscriptions?.raw_subscriptions]);

    useEffect(() => {
      if (hasActiveSubscription && userStore.customerPortalUrl) {
        window.location.href = userStore.customerPortalUrl;
      }
    }, [hasActiveSubscription, userStore.customerPortalUrl]);

    // Don't show loading state here - it will be shown in the payment modal when needed

    // Don't render anything if stripe is not available yet
    if (!stripe) {
      return null;
    }

    return (
      <div style={{ paddingBottom: "40px" }}>
        {error ? (
          <div style={{ textAlign: "center" }}>
            <div style={{ 
              color: "#df1b41", 
              marginBottom: "20px",
              fontSize: "14px",
              padding: "0 20px"
            }}>
              {error}
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 20px",
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Try Again
            </button>
          </div>
        ) : clientSecret && showPaymentElement ? (
          <Elements
            key={`${planType}-${previousSetupIntentId}`}
            stripe={stripePromise}
            options={{ appearance, clientSecret }}
          >
            <PaymentCheckoutForm
              onCouponSubmit={handleCouponSubmit}
              couponError={couponError}
              couponSuccess={couponSuccess}
              onBack={handleBack}
              setupIntentId={previousSetupIntentId}
              autoAppliedCoupon={autoAppliedCoupon}
              onAddPaymentDetails={handleAddPaymentDetails}
              isLoading={isLoading}
            />
          </Elements>
        ) : isRefreshing ? (
          // Show loading state while refreshing payment element
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
            color: "#888"
          }}>
            <img src={loadingIcon} alt="Loading..." style={{ width: "24px", height: "24px", marginRight: "12px" }} />
            Updating payment form...
          </div>
        ) : (
          <InitialCheckoutForm
            key={planType}
            onCouponSubmit={handleCouponSubmit}
            couponError={couponError}
            couponSuccess={couponSuccess}
            onAddPaymentDetails={handleAddPaymentDetails}
            isLoading={isCouponLoading}
            autoAppliedCoupon={autoAppliedCoupon}
          />
        )}
        <div
          style={{
            textAlign: "center",
            marginTop: "30px",
            marginBottom: "20px",
            fontSize: "12px",
            color: "#666",
            padding: "0 20px",
          }}
        >
          By continuing, you agree to our{" "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#fff", textDecoration: "underline" }}
          >
            Terms of Service
          </a>
        </div>
      </div>
    );
  }
);

// Export
export default StripeCheckout;
